import React from "react";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

/**
 * 新手指南（設計稿 #screen-guide，el-pm-template_4.html:2497-2536）
 *
 * 取代舊系統那個「只講當前這一頁」的 4 步導覽彈窗：彈窗會蓋住畫面、只能按
 * 下一步、關掉就再也找不回來，而且講的是畫面上有什麼，不是使用者想做什麼。
 *
 * 這一頁的三張卡各回答一個真的會被問出口的問題（我今天要做什麼／怎麼回報／
 * 字太小怎麼辦），每題三步，而且是一整頁 —— 有網址、可以收藏、不擋操作，
 * 看到一半想去試試看就直接按上一頁回來。
 *
 * ── 關於 components/onboarding/OnboardingGuide.js（11 步彈窗）────────
 * 那一支**已經是死碼**：唯一提到它的 components/settings/UserSettings.js
 * 自己沒有任何 importer，所以它現在沒有任何入口。這一頁不是「把入口從
 * UserSettings 搬過來」—— 沒有入口可搬。/Guide 就是新的入口。
 * OnboardingGuide.js 檔案照硬規則 8 保留不動。
 *
 * 整頁只有「知道了，回到今天」是 contained 主要按鈕（硬規則 1）。
 */

/**
 * 三張卡的內容照抄設計稿 L2508-2531，不要改寫成自己的說法。
 *
 * 第二張卡最後一步的「填 100% 會自動送給審核人」是對的，照設計稿 L2515 原文：
 * 後端 MyTaskService 的 AddProgressRecord 與 UpdateProgress 在 progress >= 100 時
 * 會走 WorkflowService 推到「審核中」，再由 ReviewService.AutoOpenReview
 * 開審核單、自動指派把關者並通知他（把關者核准的那一刻，ReviewService.Decide
 * 就自動把工項推到「已完成」，不用任何人再按一次「完成」）。
 * 新手指南只講「會送給審核人」就夠了 —— 誰是審核人、被 DoD 擋下怎麼辦、
 * 通過之後會自動完成，回報當下的畫面（/MyTask 滑桿上方的橫幅）會講，
 * 這裡多寫反而變成一頁規格書。這句本來就沒有叫人「核准後再按完成」，所以原文不動。
 */
const SECTIONS = [
  {
    title: "一、查看今日工作",
    steps: [
      "登入後預設進入「個人工項」頁面，最上方可看到待我審核、今日到期與已逾期的件數。",
      "查看第一張卡片「今日待辦」。",
      "點選任一列，即可進入該工項的畫面。",
    ],
  },
  {
    title: "二、回報工作進度",
    steps: [
      "進入工項後，找到「我的回報」區塊。",
      "選擇「目前進度」並填寫「本次工作內容」。",
      "點選藍色的「送出回報」按鈕。進度選擇「全部完成」（100%）時，將自動送交審核人。",
    ],
  },
  {
    title: "三、調整字體大小",
    steps: [
      "點選畫面右上角的使用者名稱，於下拉選單的「字級」選擇「小／中／大」；登入頁底部亦可調整。",
      "選擇「大」後，整頁文字（含日期）皆會放大。",
      "設定將自動保存，下次登入時沿用。",
    ],
  },
];

function GuideIndex() {
  const navigate = useNavigate();

  return (
    <>
      <Typography variant="h1" component="h1">
        新手指南
      </Typography>
      <Typography sx={{ mt: "var(--space-3)" }}>以下為三項最常用的操作，各分三個步驟。之後可隨時從右上角的使用者名稱再次開啟本指南。</Typography>

      {/* max-width 是版面尺寸不是顏色字級：一行太寬，眼睛回到下一行會找錯行 */}
      <Stack spacing={2} sx={{ mt: "var(--space-6)", maxWidth: 760 }}>
        {SECTIONS.map((s) => (
          <Card component="section" key={s.title}>
            <CardContent>
              <Typography variant="h3" component="h2">
                {s.title}
              </Typography>
              <Stack spacing={1} sx={{ mt: "var(--space-3)" }}>
                {s.steps.map((t, i) => (
                  <Typography key={t}>
                    {i + 1}. {t}
                  </Typography>
                ))}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: "var(--space-6)" }}>
        <Button variant="contained" type="button" onClick={() => navigate("/MyTask")}>
          前往個人工項
        </Button>
        {/* 這一句不是裝飾：看完三張卡還是找不到東西的人，需要一個「現在可以問誰」 */}
        <Typography variant="body2" color="text.secondary" component="span">
          如有其他問題，請聯絡資訊室（分機 168）。
        </Typography>
      </Stack>
    </>
  );
}

export default GuideIndex;
